import {
  DEFAULT_QUERY_OPTIONS,
  useGetList,
  useGetOne,
  UseQueryOptionsType,
  useUpdateOne,
} from "#hooks/adapters/adapterUtils";
import { useAuthenticatedContext } from "#src/contexts/AuthenticatedContext.helpers";
import { ExceptionUtils } from "#src/utils/exception";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@validereinc/common-components";
import { DeviceAdapter, DeviceType, Resources } from "@validereinc/domain";
import { BaseError, dateFormatter, downloadLink } from "@validereinc/utilities";
import { DEFAULT_INVALIDATE_OPTIONS } from "../adapterUtils";

export const DEVICE_QUERY_KEY = "device";

export const useListDevices = (
  apiParams: Parameters<typeof DeviceAdapter.getList>[0] = {},
  options: UseQueryOptionsType<
    Awaited<ReturnType<typeof DeviceAdapter.getList>>
  > = {}
) =>
  useQuery({
    queryKey: [DEVICE_QUERY_KEY, apiParams],
    queryFn: ({ queryKey: [_, params] }) => DeviceAdapter.getList(params),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });

export const useExportDevices = (apiParams = {}, exportName?: string) => {
  const { toast } = useToast();
  const {
    v2: { companyInfo },
  } = useAuthenticatedContext();

  const getFileName = () =>
    [
      companyInfo?.company?.name,
      exportName,
      "Device_List",
      dateFormatter(new Date()),
    ]
      .filter((part) => !!part)
      .join("_");

  return useMutation({
    mutationFn: async () => {
      const report = await DeviceAdapter.exportList(apiParams);

      if (!report?.s3_download_link) {
        throw new BaseError(`Could not fetch download URL for records export`, {
          cause: report,
        });
      }

      downloadLink(report.s3_download_link, getFileName());
    },
    onError: (error) => {
      ExceptionUtils.reportException(error, "error");
      toast.push({
        intent: "error",
        description: "Failed to export device list.",
      });
    },
  });
};

export const useDeleteDevice = (options?: {
  onSuccess?: () => void;
  onError?: () => void;
  onSettled?: () => void;
}) => {
  const { onSuccess, ...otherOptions } = options ?? {};
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (deviceId: string) => {
      try {
        await DeviceAdapter.deleteOne({ id: deviceId });
      } catch (caught) {
        console.error("Error deleting form", caught);

        throw caught;
      }
    },
    onSuccess: () => {
      if (onSuccess) onSuccess();
      return queryClient.invalidateQueries({
        queryKey: [DEVICE_QUERY_KEY],
        ...DEFAULT_INVALIDATE_OPTIONS,
      });
    },
    ...otherOptions,
  });
};

export const useGetOneDevice = useGetOne<DeviceType>(
  DeviceAdapter.getOne,
  Resources.DEVICE
);

export const useUpdateDevice = useUpdateOne<DeviceType>(
  DeviceAdapter.updateOne,
  Resources.DEVICE
);

export const useListDeviceTypes = useGetList(
  DeviceAdapter.types.getList,
  Resources.DEVICE,
  "type"
);
